import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getStory, addStoryComment, deleteStoryComment, updateStoryComment, type StoryWithCommentsApi } from '../../api/client';
import { CommentEditMeta } from '../../utils/commentMeta';

export default function StoryDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isLoggedIn, canModerate, userId, username, role, refreshProfile } = useAuth();
  const [story, setStory] = useState<StoryWithCommentsApi | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [commentBody, setCommentBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editBody, setEditBody] = useState('');
  const [editModeratorNote, setEditModeratorNote] = useState('');

  useEffect(() => {
    const numId = parseInt(id ?? '', 10);
    if (isNaN(numId)) {
      setError('Invalid story');
      setLoading(false);
      return;
    }
    getStory(numId)
      .then(setStory)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load story'))
      .finally(() => setLoading(false));
  }, [id]);

  // Ensure appRole/userId are refreshed so moderator tools appear consistently.
  useEffect(() => {
    if (!isLoggedIn) return;
    refreshProfile().catch(() => {});
  }, [isLoggedIn, refreshProfile]);

  // Admins logged in via admin_users have role='admin' in JWT — canModerate is always
  // true for them. For users-table superusers canModerate comes from appRole once profile loads.
  const isMod = canModerate || role === 'admin';

  const isCommentOwner = (commentUserId: number, commentUsername: string) =>
    (typeof userId === 'number' && Number(commentUserId) === userId) ||
    (!!username && username === commentUsername);

  const canEditComment = (commentUserId: number, commentUsername: string) =>
    isMod || isCommentOwner(commentUserId, commentUsername);

  /** Moderator editing any comment — show the optional admin note field. */
  const showModeratorNoteField = () => isMod;

  const handleDeleteComment = async (commentId: number) => {
    if (!story || !confirm('Delete this comment?')) return;
    try {
      await deleteStoryComment(story.id, commentId);
      setStory((prev) => prev ? { ...prev, comments: prev.comments.filter((c) => c.id !== commentId) } : null);
      if (editingId === commentId) {
        setEditingId(null);
        setEditBody('');
        setEditModeratorNote('');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete comment');
    }
  };

  const startEdit = (commentId: number, body: string, moderatorNote: string | null | undefined) => {
    setEditingId(commentId);
    setEditBody(body);
    setEditModeratorNote(moderatorNote ?? '');
  };

  const saveEdit = async () => {
    if (!story || editingId == null || !editBody.trim()) return;
    setError('');
    try {
      const editingRow = story.comments.find((c) => c.id === editingId);
      const showModNote = !!(
        editingRow && showModeratorNoteField()
      );
      const updated = await updateStoryComment(
        story.id,
        editingId,
        editBody.trim(),
        showModNote ? { moderator_note: editModeratorNote.trim() || null } : undefined
      );
      setStory((prev) =>
        prev
          ? {
              ...prev,
              comments: prev.comments.map((c) => (c.id === editingId ? { ...c, ...updated } : c)),
            }
          : null
      );
      setEditingId(null);
      setEditBody('');
      setEditModeratorNote('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update comment');
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!story || !commentBody.trim() || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const newComment = await addStoryComment(story.id, commentBody.trim());
      setStory((prev) => prev ? { ...prev, comments: [...prev.comments, newComment] } : null);
      setCommentBody('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-green-50 pt-4 pb-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="h-8 w-48 bg-amber-100 rounded animate-pulse mb-4" />
          <div className="h-4 w-full bg-amber-100/60 rounded animate-pulse mb-2" />
          <div className="h-4 w-full bg-amber-100/60 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (error && !story) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-green-50 pt-4 pb-12 px-4">
        <div className="max-w-2xl mx-auto">
          <button type="button" onClick={() => navigate('/')} className="text-amber-700 hover:text-amber-800 text-sm mb-4">
            ← Back to home
          </button>
          <p className="text-amber-800">{error}</p>
        </div>
      </div>
    );
  }

  if (!story) return null;

  const dateStr = story.created_at ? new Date(story.created_at).toLocaleDateString(undefined, { dateStyle: 'long' }) : '';
  const bodyText = (story.body || story.excerpt || '').replace(/\s+/g, ' ').trim();
  const words = bodyText ? bodyText.split(' ').length : 0;
  const readMinutes = Math.max(1, Math.round(words / 220));

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-orange-50/60 to-emerald-50/70 pt-4 pb-14 px-4">
      <div className="max-w-4xl mx-auto">
        <button
          type="button"
          onClick={() => navigate('/stories')}
          className="inline-flex items-center gap-2 text-amber-700 hover:text-amber-800 text-sm mb-6 bg-white/70 px-3 py-1.5 rounded-full border border-amber-100"
        >
          <i className="ri-arrow-left-line" aria-hidden />
          <span>Back to stories</span>
        </button>

        <article className="bg-white rounded-3xl border border-amber-100 shadow-[0_18px_50px_rgba(120,53,15,0.10)] overflow-hidden">
          <div className={`relative ${story.cover_image ? 'aspect-[21/8]' : 'aspect-[21/7]'} w-full overflow-hidden`}>
            {story.cover_image ? (
              <img src={story.cover_image} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-r from-amber-200 via-orange-200 to-yellow-100" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-amber-950/80 via-amber-900/20 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/15 px-3 py-1 text-xs font-medium tracking-wide text-white mb-3">
                <i className="ri-book-read-line" aria-hidden />
                African Storytelling
              </div>
              <h1 className="text-2xl md:text-4xl font-bold text-white leading-tight drop-shadow-sm" style={{ fontFamily: 'Ubuntu, sans-serif' }}>
                {story.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs md:text-sm text-amber-50/95">
                {dateStr && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-black/20 px-3 py-1 border border-white/20">
                    <i className="ri-calendar-line" aria-hidden />
                    {dateStr}
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5 rounded-full bg-black/20 px-3 py-1 border border-white/20">
                  <i className="ri-time-line" aria-hidden />
                  {readMinutes} min read
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-black/20 px-3 py-1 border border-white/20">
                  <i className="ri-chat-1-line" aria-hidden />
                  {story.comments.length} comment{story.comments.length === 1 ? '' : 's'}
                </span>
              </div>
            </div>
          </div>
          <div className="p-6 md:p-10">
            {story.excerpt && (
              <p className="text-lg md:text-xl text-amber-900/90 leading-relaxed border-l-4 border-amber-300 pl-4 mb-7 italic">
                {story.excerpt}
              </p>
            )}
            <div className="prose prose-amber prose-p:text-stone-700 prose-headings:text-amber-950 prose-strong:text-amber-900 max-w-none whitespace-pre-wrap text-[1.05rem] leading-8">
              {story.body || story.excerpt}
            </div>
            <div className="mt-8 pt-6 border-t border-amber-100 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-stone-500">
                Shared from Frequency Locus African Storytelling.
              </p>
              <button
                type="button"
                onClick={() => navigate('/stories')}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-100 text-amber-900 text-sm hover:bg-amber-200"
              >
                More stories
                <i className="ri-arrow-right-line" aria-hidden />
              </button>
            </div>
          </div>
        </article>

        <section className="mt-10 rounded-[2rem] border border-stone-200/70 bg-white/85 p-5 shadow-sm md:p-6">
          <h2 className="text-lg font-semibold text-stone-900">Story conversation</h2>
          <p className="mt-1 text-sm text-stone-500">
            Share your thoughts on this story{story.comments.length > 0 ? ` (${story.comments.length})` : ''}.
          </p>
          {story.comments.length === 0 ? (
            <p className="mt-4 text-sm text-stone-500">No comments yet. Be the first to share your thoughts.</p>
          ) : (
            <div className="mt-4 space-y-3 max-h-[28rem] overflow-y-auto pr-1">
              {story.comments.map((c) => (
                <div
                  key={c.id}
                  className={`rounded-[1.25rem] p-3 ${
                    c.is_moderator_comment ? 'bg-amber-50/90 ring-1 ring-amber-200/60' : 'bg-stone-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 flex flex-wrap items-center gap-2">
                      <span>{c.username}</span>
                      {c.is_moderator_comment && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-200/70 px-2 py-0.5 text-[10px] uppercase tracking-wide text-amber-900 normal-case font-medium">
                          <i className="ri-shield-check-line text-[11px]" aria-hidden />
                          Moderator
                        </span>
                      )}
                    </p>
                    {canEditComment(c.user_id, c.username) && editingId !== c.id && (
                      <span className="flex shrink-0 gap-0.5">
                        <button
                          type="button"
                          onClick={() => startEdit(c.id, c.body, c.moderator_note)}
                          className="rounded-lg p-1 text-stone-400 hover:bg-amber-50 hover:text-amber-800"
                          title="Edit"
                        >
                          <i className="ri-pencil-line text-sm" aria-hidden />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteComment(c.id)}
                          className="rounded-lg p-1 text-stone-400 hover:bg-red-50 hover:text-red-600"
                          title="Delete"
                        >
                          <i className="ri-delete-bin-line text-sm" aria-hidden />
                        </button>
                      </span>
                    )}
                  </div>
                  {editingId === c.id ? (
                    <div className="mt-2 space-y-2">
                      <textarea
                        value={editBody}
                        onChange={(e) => setEditBody(e.target.value)}
                        rows={3}
                        className="w-full resize-y min-h-[72px] rounded-xl border border-stone-200 px-3 py-2 text-sm focus:border-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-100"
                      />
                      {showModeratorNoteField() && (
                        <div>
                          <label className="block text-xs font-medium text-stone-600 mb-1">
                            Comment by admin (optional)
                          </label>
                          <input
                            type="text"
                            value={editModeratorNote}
                            onChange={(e) => setEditModeratorNote(e.target.value)}
                            placeholder="e.g. why the comment was edited"
                            className="w-full rounded-xl border border-stone-200 bg-stone-50/80 px-3 py-2 text-sm focus:border-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-100"
                          />
                        </div>
                      )}
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={saveEdit}
                          className="rounded-lg bg-stone-900 px-3 py-1.5 text-xs font-medium text-white"
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingId(null);
                            setEditBody('');
                            setEditModeratorNote('');
                          }}
                          className="rounded-lg border border-stone-200 px-3 py-1.5 text-xs text-stone-600"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="mt-2 text-sm text-stone-700">{c.body}</p>
                      {c.moderator_note ? (
                        <p className="mt-2 rounded-xl border border-stone-200 bg-stone-100/80 px-3 py-2 text-xs text-stone-800">
                          <span className="font-semibold">Comment by admin:</span> {c.moderator_note}
                        </p>
                      ) : null}
                      <CommentEditMeta
                        createdAt={c.created_at}
                        editedAt={c.edited_at}
                        editCount={c.edit_count}
                        lastEditedByAdmin={c.last_edited_by_admin}
                        className="text-xs text-stone-400 mt-1.5"
                      />
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {isLoggedIn ? (
            <form onSubmit={handleSubmitComment} className="mt-6">
              {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={commentBody}
                  onChange={(e) => setCommentBody(e.target.value)}
                  placeholder="Write a comment..."
                  className="flex-1 rounded-2xl border border-stone-200 px-4 py-3 text-sm focus:border-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-100"
                  required
                />
                <button
                  type="submit"
                  disabled={submitting || !commentBody.trim()}
                  className="rounded-2xl bg-stone-900 px-4 py-3 text-sm font-medium text-white disabled:opacity-50"
                >
                  {submitting ? 'Posting…' : 'Post'}
                </button>
              </div>
            </form>
          ) : (
            <p className="mt-6 text-sm text-stone-500">
              <button type="button" onClick={() => navigate('/login')} className="text-amber-700 hover:underline">
                Log in
              </button>
              {' '}to comment.
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
